import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import _ from "lodash";

import * as Api from "@modules/api";

import {
  getStarted,
  getCompleted,
  getFavorite,
  getBookmarked,
  getHighlighted,
  getBookmarkedDetails,
  getHighlightedDetails,
  getMyCertificates,
  getPaginatedPlayLists,
  createPlayList,
  getPlayListById,
  removeItemFromPlaylist,
  getPlayLists,
  moveToPlaylist,
  removePlaylist,
  updatePlayList,
  addItemToPlaylist,
  reArrangePlaylistItems,
  addToFavorite,
  removeFromFavorite,
  getFavoriteContentEntitiyById,
} from "./thunks";
import { MyLibraryState } from "./types";

export const name = "myLibrary";

const initialState: MyLibraryState = {
  started: {
    data: [],
    meta: {
      hasNextPage: undefined,
      currentPage: undefined,
      totalCount: undefined,
    },
    status: "idle",
  },
  completed: {
    data: [],
    meta: {
      hasNextPage: undefined,
      currentPage: undefined,
      totalCount: undefined,
    },
    status: "idle",
  },
  favorite: {
    data: [],
    meta: {
      hasNextPage: undefined,
      currentPage: undefined,
      totalCount: undefined,
    },
    status: "idle",
  },
  bookmarked: {
    data: [],
    meta: {
      hasNextPage: undefined,
      currentPage: undefined,
      totalCount: undefined,
    },
    status: "idle",
  },
  highlighted: {
    data: [],
    meta: {
      hasNextPage: undefined,
      currentPage: undefined,
      totalCount: undefined,
    },
    status: "idle",
  },
  playlists: {
    data: [],
    status: "idle",
  },
  paginatedplaylists: {
    data: [],
    meta: {
      hasNextPage: undefined,
      currentPage: undefined,
      totalCount: undefined,
    },
    status: "idle",
  },
  bookmarkedDetails: {
    data: [],
    status: "idle",
  },
  highlightedDetails: {
    data: [],
    status: "idle",
  },
  myCertificates: {
    data: null,
    status: "idle",
  },
  createPlaylist: {
    data: null,
    status: "idle",
  },
  updatePlaylist: {
    status: "idle",
  },
  playlistDetails: {
    data: null,
    status: "idle",
  },
  removePlaylist: { status: "idle" },
  addItemToPlaylist: { status: "idle" },
  removeItemFromPlaylist: { status: "idle" },
  moveToPlaylist: { status: "idle" },
  arrangedplaylistItems: {
    status: "idle",
  },
  addToFavorite: { status: "idle" },
  removeFromFavorite: { status: "idle" },
  favoriteContentEntitiy: { status: "idle", data: undefined },
};

export const getStartedAsync = createAsyncThunk(
  name + "/getStarted",
  getStarted
);

export const getCompletedAsync = createAsyncThunk(
  name + "/getCompleted",
  getCompleted
);

export const getFavoriteAsync = createAsyncThunk(
  name + "/getFavorite",
  getFavorite
);

export const getBookmarkedAsync = createAsyncThunk(
  name + "/getBookmarked",
  getBookmarked
);

export const getHighlightedAsync = createAsyncThunk(
  name + "/getHighlighted",
  getHighlighted
);

export const getBookmarkedDetailsAsync = createAsyncThunk(
  name + "/getBookmarkedDetails",
  getBookmarkedDetails
);

export const getHighlightedDetailsAsync = createAsyncThunk(
  name + "/getHighlightedDetails",
  getHighlightedDetails
);

export const getMyCertificatesAsync = createAsyncThunk(
  name + "/getMyCertificates",
  getMyCertificates
);

export const getPlaylistsAsync = createAsyncThunk(
  name + "/getPlaylists",
  getPlayLists
);

export const getPaginatedPlaylistsAsync = createAsyncThunk(
  name + "/getPaginatedPlaylists",
  getPaginatedPlayLists
);

export const createPlaylistAsync = createAsyncThunk(
  name + "/createPlaylist",
  createPlayList
);

export const updatePlaylistAsync = createAsyncThunk(
  name + "/updatePlaylist",
  updatePlayList
);

export const getPlaylistByIdAsync = createAsyncThunk(
  name + "/getPlaylistById",
  getPlayListById
);

export const removePlaylistAsync = createAsyncThunk(
  name + "/removePlaylist",
  removePlaylist
);

export const addItemToPlaylistAsync = createAsyncThunk(
  name + "/addItemToPlaylist",
  addItemToPlaylist
);

export const removeItemFromPlaylistAsync = createAsyncThunk(
  name + "/removeItemFromPlaylist",
  removeItemFromPlaylist
);

export const moveToPlaylistAsync = createAsyncThunk(
  name + "/moveToPlaylist",
  moveToPlaylist
);

export const reArrangePlaylistItemsAsync = createAsyncThunk(
  name + "/reArrangePlaylistItems",
  reArrangePlaylistItems
);

export const addToFavoriteAsync = createAsyncThunk(
  name + "/addToFavorite",
  addToFavorite
);

export const removeFromFavoriteAsync = createAsyncThunk(
  name + "/removeFromFavorite",
  removeFromFavorite
);

export const getFavoriteContentEntitiyByIdAsync = createAsyncThunk(
  name + "/getFavoriteContentEntitiyById",
  getFavoriteContentEntitiyById
);

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: (_) => {
      return initialState;
    },
    clearStarted: (state) => {
      state.started = initialState.started;
    },
    clearCompleted: (state) => {
      state.completed = initialState.completed;
    },
    clearFollowed: (state) => {
      state.favorite = initialState.favorite;
    },
    clearBookmarked: (state) => {
      state.bookmarked = initialState.bookmarked;
    },
    clearHighlighted: (state) => {
      state.highlighted = initialState.highlighted;
    },
    clearBookmarkedDetails: (state) => {
      state.bookmarkedDetails = initialState.bookmarkedDetails;
    },
    clearHighlightedDetails: (state) => {
      state.highlightedDetails = initialState.highlightedDetails;
    },
    resetSuccessAlert: (state) => {
      state.addItemToPlaylist.status = initialState.addItemToPlaylist.status;
      state.createPlaylist.status = initialState.createPlaylist.status;
      state.arrangedplaylistItems.status =
        initialState.arrangedplaylistItems.status;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStartedAsync.pending, (state) => {
        state.started.status = "loading";
      })
      .addCase(getStartedAsync.fulfilled, (state, action) => {
        const stateObj = _.reduce(
          state.started.data,
          function (
            obj: { [key: string]: Api.Server.LibraryItemOutput },
            param
          ) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const dataObj = _.reduce(
          action.payload.data,
          function (
            obj: { [key: string]: Api.Server.LibraryItemOutput },
            param
          ) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const totalObj = {
          ...stateObj,
          ...dataObj,
        };

        state.started = {
          data: Object.values(totalObj),
          meta: action.payload.meta,
          status: "idle",
        };
      })
      .addCase(getStartedAsync.rejected, (state) => {
        state.started.status = "failed";
      })
      .addCase(getCompletedAsync.pending, (state) => {
        state.completed.status = "loading";
      })
      .addCase(getCompletedAsync.fulfilled, (state, action) => {
        const stateObj = _.reduce(
          state.completed.data,
          function (
            obj: { [key: string]: Api.Server.LibraryItemOutput },
            param
          ) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const dataObj = _.reduce(
          action.payload.data,
          function (
            obj: { [key: string]: Api.Server.LibraryItemOutput },
            param
          ) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const totalObj = {
          ...stateObj,
          ...dataObj,
        };

        state.completed = {
          data: Object.values(totalObj),
          meta: action.payload.meta,
          status: "idle",
        };
      })
      .addCase(getCompletedAsync.rejected, (state) => {
        state.completed.status = "failed";
      })
      .addCase(getFavoriteAsync.pending, (state) => {
        state.favorite.status = "loading";
      })
      .addCase(getFavoriteAsync.fulfilled, (state, action) => {
        const stateObj = _.reduce(
          state.favorite.data,
          function (
            obj: { [key: string]: Api.Server.LibraryItemOutput },
            param
          ) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const dataObj = _.reduce(
          action.payload.data,
          function (
            obj: { [key: string]: Api.Server.LibraryItemOutput },
            param
          ) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const totalObj = {
          ...stateObj,
          ...dataObj,
        };

        state.favorite = {
          data: Object.values(totalObj),
          meta: action.payload.meta,
          status: "idle",
        };
      })
      .addCase(getFavoriteAsync.rejected, (state) => {
        state.favorite.status = "failed";
      })
      .addCase(getBookmarkedAsync.pending, (state) => {
        state.bookmarked.status = "loading";
      })
      .addCase(getBookmarkedAsync.fulfilled, (state, action) => {
        const stateObj = _.reduce(
          state.bookmarked.data,
          function (
            obj: { [key: string]: Api.Server.LibraryItemOutput },
            param
          ) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const dataObj = _.reduce(
          action.payload.data,
          function (
            obj: { [key: string]: Api.Server.LibraryItemOutput },
            param
          ) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const totalObj = {
          ...stateObj,
          ...dataObj,
        };

        state.bookmarked = {
          data: Object.values(totalObj),
          meta: action.payload.meta,
          status: "idle",
        };
      })
      .addCase(getBookmarkedAsync.rejected, (state) => {
        state.bookmarked.status = "failed";
      })
      .addCase(getHighlightedAsync.pending, (state) => {
        state.highlighted.status = "loading";
      })
      .addCase(getHighlightedAsync.fulfilled, (state, action) => {
        const stateObj = _.reduce(
          state.highlighted.data,
          function (
            obj: { [key: string]: Api.Server.LibraryItemOutput },
            param
          ) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const dataObj = _.reduce(
          action.payload.data,
          function (
            obj: { [key: string]: Api.Server.LibraryItemOutput },
            param
          ) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const totalObj = {
          ...stateObj,
          ...dataObj,
        };

        state.highlighted = {
          data: Object.values(totalObj),
          meta: action.payload.meta,
          status: "idle",
        };
      })
      .addCase(getHighlightedAsync.rejected, (state) => {
        state.highlighted.status = "failed";
      })
      .addCase(getBookmarkedDetailsAsync.pending, (state) => {
        state.bookmarkedDetails.status = "loading";
      })
      .addCase(getBookmarkedDetailsAsync.fulfilled, (state, action) => {
        state.bookmarkedDetails = {
          data: action.payload,
          status: "idle",
        };
      })
      .addCase(getBookmarkedDetailsAsync.rejected, (state) => {
        state.bookmarkedDetails.status = "failed";
      })
      .addCase(getHighlightedDetailsAsync.pending, (state) => {
        state.highlightedDetails.status = "loading";
      })
      .addCase(getHighlightedDetailsAsync.fulfilled, (state, action) => {
        state.highlightedDetails = {
          data: action.payload,
          status: "idle",
        };
      })
      .addCase(getHighlightedDetailsAsync.rejected, (state) => {
        state.highlightedDetails.status = "failed";
      })
      .addCase(getMyCertificatesAsync.pending, (state) => {
        state.myCertificates.status = "loading";
      })
      .addCase(getMyCertificatesAsync.fulfilled, (state, action) => {
        state.myCertificates = {
          data: action.payload,
          status: "idle",
        };
      })
      .addCase(getMyCertificatesAsync.rejected, (state) => {
        state.myCertificates.status = "failed";
      })
      .addCase(getPlaylistsAsync.pending, (state) => {
        state.playlists.status = "loading";
      })
      .addCase(getPlaylistsAsync.fulfilled, (state, action) => {
        state.playlists = {
          data: action.payload,
          status: "idle",
        };
      })
      .addCase(getPlaylistsAsync.rejected, (state) => {
        state.playlists.status = "failed";
      })
      .addCase(getPaginatedPlaylistsAsync.pending, (state) => {
        state.paginatedplaylists.status = "loading";
      })
      .addCase(getPaginatedPlaylistsAsync.fulfilled, (state, action) => {
        state.paginatedplaylists = {
          data: action.payload.data,
          meta: action.payload.meta,
          status: "idle",
        };
      })
      .addCase(getPaginatedPlaylistsAsync.rejected, (state) => {
        state.paginatedplaylists.status = "failed";
      })
      .addCase(createPlaylistAsync.pending, (state) => {
        state.createPlaylist.status = "loading";
      })
      .addCase(createPlaylistAsync.fulfilled, (state, action) => {
        state.createPlaylist = {
          data: action.payload,
          status: "success",
        };
      })
      .addCase(createPlaylistAsync.rejected, (state) => {
        state.createPlaylist.status = "failed";
      })
      .addCase(updatePlaylistAsync.pending, (state) => {
        state.updatePlaylist.status = "loading";
      })
      .addCase(updatePlaylistAsync.fulfilled, (state, action) => {
        state.updatePlaylist.status = "idle";
      })
      .addCase(updatePlaylistAsync.rejected, (state) => {
        state.updatePlaylist.status = "failed";
      })
      .addCase(getPlaylistByIdAsync.pending, (state) => {
        state.playlistDetails.status = "loading";
      })
      .addCase(getPlaylistByIdAsync.fulfilled, (state, action) => {
        state.playlistDetails = {
          data: action.payload,
          status: "idle",
        };
      })
      .addCase(getPlaylistByIdAsync.rejected, (state) => {
        state.playlistDetails.status = "failed";
      })
      .addCase(addItemToPlaylistAsync.pending, (state) => {
        state.addItemToPlaylist.status = "loading";
      })
      .addCase(addItemToPlaylistAsync.fulfilled, (state) => {
        state.addItemToPlaylist.status = "success";
      })
      .addCase(addItemToPlaylistAsync.rejected, (state) => {
        state.addItemToPlaylist.status = "failed";
      })
      .addCase(removeItemFromPlaylistAsync.pending, (state) => {
        state.removeItemFromPlaylist.status = "loading";
      })
      .addCase(removeItemFromPlaylistAsync.fulfilled, (state) => {
        state.removeItemFromPlaylist.status = "idle";
      })
      .addCase(removeItemFromPlaylistAsync.rejected, (state) => {
        state.removeItemFromPlaylist.status = "failed";
      })
      .addCase(moveToPlaylistAsync.pending, (state) => {
        state.moveToPlaylist.status = "loading";
      })
      .addCase(moveToPlaylistAsync.fulfilled, (state) => {
        state.moveToPlaylist.status = "idle";
      })
      .addCase(moveToPlaylistAsync.rejected, (state) => {
        state.moveToPlaylist.status = "failed";
      })
      .addCase(removePlaylistAsync.pending, (state) => {
        state.removePlaylist.status = "loading";
      })
      .addCase(removePlaylistAsync.fulfilled, (state) => {
        state.removePlaylist.status = "idle";
      })
      .addCase(removePlaylistAsync.rejected, (state) => {
        state.removePlaylist.status = "failed";
      })
      .addCase(reArrangePlaylistItemsAsync.pending, (state) => {
        state.arrangedplaylistItems.status = "loading";
      })
      .addCase(reArrangePlaylistItemsAsync.fulfilled, (state, action) => {
        state.arrangedplaylistItems.status = "success";
      })
      .addCase(reArrangePlaylistItemsAsync.rejected, (state) => {
        state.arrangedplaylistItems.status = "failed";
      })
      .addCase(addToFavoriteAsync.pending, (state) => {
        state.addToFavorite.status = "loading";
      })
      .addCase(addToFavoriteAsync.fulfilled, (state) => {
        state.addToFavorite.status = "success";
      })
      .addCase(addToFavoriteAsync.rejected, (state) => {
        state.addToFavorite.status = "failed";
      })
      .addCase(removeFromFavoriteAsync.pending, (state) => {
        state.removeFromFavorite.status = "loading";
      })
      .addCase(removeFromFavoriteAsync.fulfilled, (state) => {
        state.removeFromFavorite.status = "success";
      })
      .addCase(removeFromFavoriteAsync.rejected, (state) => {
        state.removeFromFavorite.status = "failed";
      })
      .addCase(getFavoriteContentEntitiyByIdAsync.pending, (state) => {
        state.favoriteContentEntitiy.status = "loading";
      })
      .addCase(
        getFavoriteContentEntitiyByIdAsync.fulfilled,
        (state, action) => {
          state.favoriteContentEntitiy = {
            data: action.payload.data,
            status: "idle",
          };
        }
      )
      .addCase(getFavoriteContentEntitiyByIdAsync.rejected, (state) => {
        state.favoriteContentEntitiy.status = "failed";
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
